import { COMMENT_TYPE, STATUS_TYPE } from '../global/constants';
import { createNewComment, updateTaskDetails } from './taskServices';
import { trackAnalyticActivity } from '../services/analyticsService';
import { TASK_ANALYTICS } from '../global/analyticsConstants';
import { StatusListInterface } from '../interfaces/ProjectInterface';
import { TaskModel } from '../mobx/models/task';
import { getRawComment } from '../helpers/common';
import { nanoid } from 'nanoid';
import { rootStore } from '../mobx/rootStore';

export const checkSubtaskStatus = (task: TaskModel) => {
  let allSubTasksStatus = true;
  if (task?.subTasks && task?.subTasks?.length > 0) {
    for (const subTask of task.subTasks) {
      if (subTask?.status === false) {
        allSubTasksStatus = false;
        break;
      }
    }
  }
  return allSubTasksStatus;
};

export const dispatchTaskUpdates = async (item: TaskModel, value: boolean) => {
  const currentUser = rootStore.currentUser;
  if (!currentUser) return;
  const payloadTask = { status: value, projectId: item?.projectId };
  const newComment = {
    id: nanoid(),
    type: COMMENT_TYPE.STATUS_CHANGE,
    status: value
  };
  const updatedComment = getRawComment(newComment, currentUser);
  item.upsertComment(updatedComment);
  // Task update and its status-change comment are independent, so run them together.
  await Promise.all([
    updateTaskDetails(item?.id, payloadTask),
    createNewComment(item?.id, {
      Type: COMMENT_TYPE.STATUS_CHANGE,
      Status: value
    })
  ]);
  if (item?.subTasks && item?.subTasks?.length > 0 && value) {
    // Run every subtask update and its comment in one parallel batch.
    await Promise.all(
      item.subTasks.flatMap((subTask) => [
        updateTaskDetails(subTask.id, payloadTask),
        createNewComment(subTask.id, {
          Type: COMMENT_TYPE.STATUS_CHANGE,
          Status: true
        })
      ])
    );
  }

  if (true) {
    if (value) trackAnalyticActivity(TASK_ANALYTICS.MARKED_DONE);
    else {
      trackAnalyticActivity(TASK_ANALYTICS.REOPENED);
    }
  }
};

export const onUpdateStatus = async (
  task: TaskModel,
  status: StatusListInterface,
  previousStatus: StatusListInterface,
  isSubTaskUpdate: boolean
) => {
  // Return if status is not changed
  if (status?.['_id'] === previousStatus?.['_id']) {
    return;
  }
  //find subtasks that need to be updated
  const subTasksToUpdate =
    isSubTaskUpdate && task?.subTasks?.length
      ? task.subTasks.filter((subTask) => subTask?.statusDetails?.['_id'] !== status?.['_id'])
      : [];

  task.setStatus(status['_id'], isSubTaskUpdate);
  const currentUser = rootStore.currentUser;
  if (!currentUser) return;
  const payloadTask = { statusId: status['_id'], projectId: task?.projectId };

  if (isSubTaskUpdate && subTasksToUpdate.length > 0) {
    const updateResults = await Promise.all(
      subTasksToUpdate.map((subTask) => updateTaskDetails(subTask.id, payloadTask))
    );
    // Roll back the optimistic status for any subtask whose update failed.
    const failedSubTasks = subTasksToUpdate.filter((_, index) => !updateResults[index]);
    if (failedSubTasks.length > 0) {
      failedSubTasks.forEach((subTask) => {
        subTask.statusId = previousStatus['_id'];
        subTask.status = previousStatus?.Type === STATUS_TYPE.COMPLETED;
      });
    }
    // Only post activity comments for subtasks that were updated successfully.
    const succeededSubTasks = subTasksToUpdate.filter((_, index) => updateResults[index]);
    await Promise.all(
      succeededSubTasks.map((subTask) =>
        createNewComment(subTask.id, {
          Type: COMMENT_TYPE.STATUS_ID_CHANGE,
          Group: status?.Name,
          Priority: status?.Type
        })
      )
    );
  }

  const newComment = {
    id: nanoid(),
    type: COMMENT_TYPE.STATUS_ID_CHANGE,
    group: status?.Name,
    previousGroup: previousStatus?.Name,
    priority: status?.Type
  };
  const updatedComment = getRawComment(newComment, currentUser);
  task.upsertComment(updatedComment);
  const isUpdated = await updateTaskDetails(task?.id, payloadTask);
  // The server update failed (updateTaskDetails returns false instead of throwing).
  // Revert the optimistic status change and remove the fabricated activity comment.
  if (!isUpdated) {
    task.setStatus(previousStatus['_id'], isSubTaskUpdate);
    task.removeComment(updatedComment.id);
    return;
  }
  await createNewComment(task?.id, {
    Type: COMMENT_TYPE.STATUS_ID_CHANGE,
    Group: status?.Name,
    PreviousGroup: previousStatus?.Name,
    Priority: status?.Type
  });
  trackAnalyticActivity(TASK_ANALYTICS.STATUS_CHANGED);
};
